import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { Subject } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';
import { AuthService } from '../auth/auth.service';
import { RealtimeChannel } from '@supabase/supabase-js';
export interface OrderNotification { id: string; order_number?: string; total_amount?: number; created_at: string; }
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private auth = inject(AuthService);
  unreadOrders = signal<OrderNotification[]>([]);
  pendingTotal = signal(0);
  connection = signal('Connecting');
  lastEventAt = signal<string | null>(null);
  lastSyncedAt = signal<string | null>(null);
  stale = signal(true);
  readonly recovered = new Subject<void>();
  private channel: RealtimeChannel | null = null;
  private syncing = false;
  private live = false;
  private generation = 0;
  constructor() {
    effect(onCleanup => {
      const user = this.auth.user();
      const store = this.auth.storeId();
      const generation = ++this.generation;
      this.lastSyncedAt.set(null);
      this.unreadOrders.set([]);
      this.pendingTotal.set(0);
      this.stale.set(true);
      this.live = false;
      if (!user || !this.auth.hasStoreOperations()) {
        this.connection.set('Task workspace');
        this.stale.set(false);
        return;
      }
      this.connection.set('Connecting');
      this.channel = supabase.channel(`admin-recovery:${user.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders',
          ...(user.role === 'store_manager' && store ? { filter: `store_id=eq.${store}` } : {}) }, () => {
          if (generation !== this.generation) return;
          this.lastEventAt.set(new Date().toISOString());
          void this.refresh(generation);
        }).subscribe(status => {
          if (generation !== this.generation) return;
          this.live = status === 'SUBSCRIBED';
          this.stale.set(true);
          this.connection.set(this.live ? 'Synchronizing' : 'Live updates disconnected; reconnecting');
          if (this.live) void this.refresh(generation);
        });
      const interval = setInterval(() => void this.refresh(generation), 30000);
      const foreground = () => { if (document.visibilityState === 'visible') void this.refresh(generation); };
      document.addEventListener('visibilitychange', foreground);
      window.addEventListener('online', foreground);
      untracked(() => void this.refresh(generation));
      const channel = this.channel;
      onCleanup(() => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', foreground);
        window.removeEventListener('online', foreground);
        void supabase.removeChannel(channel);
      });
    });
  }
  async refresh(generation = this.generation): Promise<void> {
    if (this.syncing || !this.auth.isLoggedIn() || !this.auth.hasStoreOperations()) return;
    this.syncing = true;
    try {
      const { data, error } = await supabase.rpc('admin_order_recovery_snapshot', { p_since: this.lastSyncedAt() });
      if (error) throw error;
      if (generation !== this.generation) return;
      this.unreadOrders.set(data.pending);
      this.pendingTotal.set(Number(data.pending_total));
      this.lastSyncedAt.set(data.synced_at);
      this.stale.set(!this.live);
      this.connection.set(this.live ? 'Live · synchronized' : 'Live updates disconnected · refreshed by fallback');
      this.recovered.next();
    } catch {
      if (generation === this.generation) {
        this.stale.set(true);
        this.connection.set('Order sync failed; retrying every 30 seconds');
      }
    } finally { this.syncing = false; }
  }
  clearUnread(): void { this.unreadOrders.set([]); }
}
